import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { skillsApi } from "@/api/skills";
import { queryKeys } from "@/lib/query-keys";
import { Globe, Zap, Plus, Trash2, User } from "lucide-react";

interface TenantSkill {
  id: string;
  companyId: string;
  catalogueSkillId: string | null;
  name: string;
  enabled: boolean;
  scope: "agent" | "global";
}

export function TenantSkillsSettings() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [selectedSkill, setSelectedSkill] = useState("");

  const { data: skillCatalogue = [] } = useQuery({
    queryKey: queryKeys.skillCatalogue.all,
    queryFn: skillsApi.listCatalogue,
  });
  const { data: tenantSkills = [] } = useQuery({
    queryKey: queryKeys.tenantSkills.byCompany(tenantId),
    queryFn: () => skillsApi.listTenantSkills(tenantId) as Promise<TenantSkill[]>,
  });

  const assignedSkillIds = new Set(tenantSkills.map((t) => t.catalogueSkillId));
  const availableSkills = skillCatalogue.filter((c) => !assignedSkillIds.has(c.id));

  const assignSkill = useMutation({
    mutationFn: (catalogueSkillId: string) => {
      const cat = skillCatalogue.find((c) => c.id === catalogueSkillId);
      return skillsApi.assignTenantSkill(tenantId, {
        companyId: tenantId,
        catalogueSkillId,
        name: cat?.name || catalogueSkillId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSkills.byCompany(tenantId) });
      setSelectedSkill("");
    },
  });

  const removeSkill = useMutation({
    mutationFn: (id: string) => skillsApi.removeTenantSkill(tenantId, id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSkills.byCompany(tenantId) }),
  });

  /**
   * Flip scope between "agent" (only assigned agents load this skill) and
   * "global" (every agent in the tenant loads it). The PATCH triggers an
   * eager fan-out syncSkills across the tenant on the server, so the
   * SKILL.md file lands in every agent's workspace before they next take
   * a turn.
   */
  const toggleScope = useMutation({
    mutationFn: (skill: TenantSkill) =>
      skillsApi.updateTenantSkill(tenantId, skill.id, {
        scope: skill.scope === "global" ? "agent" : "global",
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSkills.byCompany(tenantId) }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Zap size={18} />
          Skills ({tenantSkills.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {availableSkills.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <select
              value={selectedSkill}
              onChange={(e) => setSelectedSkill(e.target.value)}
              className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
            >
              <option value="">Select Skill to assign…</option>
              {availableSkills.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName || c.name} ({c.skillType})
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={!selectedSkill || assignSkill.isPending}
              onClick={() => assignSkill.mutate(selectedSkill)}
            >
              <Plus size={14} className="mr-1" />
              Assign
            </Button>
          </div>
        )}

        {tenantSkills.length === 0 ? (
          <p className="text-sm text-text-muted">No skills assigned to this tenant.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {tenantSkills.map((skill) => {
              const isGlobal = skill.scope === "global";
              return (
                <div
                  key={skill.id}
                  className="flex items-center justify-between rounded px-2 py-2 hover:bg-surface-hover"
                >
                  <div className="flex items-center gap-2">
                    <Zap size={14} className="text-accent" />
                    <span className="text-sm text-text-primary">{skill.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={toggleScope.isPending}
                      onClick={() => toggleScope.mutate(skill)}
                      title={
                        isGlobal
                          ? "Currently loaded by every agent in this tenant. Click to make it agent-scoped (only assigned agents load it)."
                          : "Currently only loaded by agents that have it assigned. Click to make it tenant-global (every agent loads it)."
                      }
                      className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] text-text-secondary hover:bg-surface-hover disabled:opacity-50"
                    >
                      {isGlobal ? <Globe size={11} /> : <User size={11} />}
                      {isGlobal ? "global" : "agent"}
                    </button>
                    <Badge variant={skill.enabled ? "success" : "default"}>
                      {skill.enabled ? "enabled" : "disabled"}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => removeSkill.mutate(skill.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
